import { Box, Flex, type HTMLStyledProps } from '@/styled-system/jsx'
import { formatShort } from '@/utils/formatNumber'
import chroma from 'chroma-js'
import { forwardRef } from 'react'

const createColors = (main: string, border: string) => {
  const c = chroma(main)

  return {
    main,
    dark: c.darken(2).hex(),
    border,
  }
}

const CHIP_COLORS = {
  1: createColors('#E6E3E8', '#644B72'),
  10: createColors('#DE3449', '#FFFFFF'),
  100: createColors('#E5923C', '#FFFFFF'),
  1e3: createColors('#F7D352', '#FFFFFF'),
  1e4: createColors('#24D861', '#FFFFFF'),
  1e5: createColors('#248CD8', '#FFFFFF'),
  1e6: createColors('#9424D8', '#FFFFFF'),
}

export type ChipProps = HTMLStyledProps<'div'> & {
  children?: React.ReactNode
  isDisabled?: boolean
  isSelected?: boolean
  value: number
}

const Chip = forwardRef<HTMLDivElement, ChipProps>(function Chip(
  { children, isDisabled, isSelected, value, ...props },
  ref
) {
  const color = CHIP_COLORS[value as keyof typeof CHIP_COLORS]

  let boxShadow = `${color.dark} 0 0.125em`

  if (isSelected) {
    boxShadow += ', rgb(255, 255, 255) 0 0.065em 0 0.25em'
  }

  return (
    <Box
      ref={ref}
      position='relative'
      flexShrink={0}
      w='2em'
      h='2em'
      color='neutral.100'
      borderRadius='50%'
      overflow='hidden'
      cursor='pointer'
      style={{
        backgroundColor: color.main,
        boxShadow,
      }}
      {...(isDisabled && {
        opacity: 0.5,
        cursor: 'not-allowed',
        pointerEvents: 'none',
      })}
      {...props}
    >
      <Flex
        align='center'
        justify='center'
        position='absolute'
        top='50%'
        left='50%'
        transform='translate(-50%, -50%)'
        fontSize='0.75em'
        lineHeight='none'
        fontWeight='bold'
        userSelect='none'
        pointerEvents='none'
        w='70%'
        h='70%'
        bg='rgba(0, 0, 0, 0.1)'
        borderRadius='full'
        boxShadow='0 -0.0625em rgba(0, 0, 0, 0.3)'
      >
        {children ?? formatShort(value)}
      </Flex>

      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 30.59 30.59'
        fill={color.border}
        fillOpacity={0.8}
        width='100%'
        height='100%'
      >
        <path d='M17.61,3.59V.19A16,16,0,0,0,15.3,0,15.88,15.88,0,0,0,13,.19v3.4Z' />
        <path d='M8.65,5.38,6.25,3a16.54,16.54,0,0,0-1.77,1.5A16.54,16.54,0,0,0,3,6.25l2.4,2.4Z' />
        <path d='M3.59,13H.19A15.88,15.88,0,0,0,0,15.3a16,16,0,0,0,.19,2.31h3.4Z' />
        <path d='M5.38,21.94,3,24.34a14.78,14.78,0,0,0,1.5,1.77,15.46,15.46,0,0,0,1.77,1.5l2.4-2.4Z' />
        <path d='M13,27V30.4a14.14,14.14,0,0,0,4.62,0V27Z' />
        <path d='M21.94,25.21l2.4,2.4a14.1,14.1,0,0,0,3.27-3.27l-2.4-2.4Z' />
        <path d='M27,17.61H30.4a14.14,14.14,0,0,0,0-4.62H27Z' />
        <path d='M25.21,8.65l2.4-2.4a15.46,15.46,0,0,0-1.5-1.77A14.78,14.78,0,0,0,24.34,3l-2.4,2.4Z' />
      </svg>
    </Box>
  )
})

export default Chip
